/**
 * Geolocation watcher with the quirks of real devices handled:
 *  - Android head units often deliver fixes with no heading/speed; we derive
 *    both from successive positions when missing.
 *  - The first fix can be a stale cached one; we ask for maximumAge 0 and
 *    filter fixes that are wildly inaccurate.
 *  - watchPosition silently stops on some devices when the tab was backgrounded;
 *    we restart the watch when visibility returns.
 */

import type { Fix } from '../core/types';
import { bearing, distance } from '../core/geo';
import { t } from '../core/i18n';

export interface GpsEvents {
  onFix: (fix: Fix) => void;
  onError: (message: string) => void;
}

const MAX_ACCURACY_METRES = 150;

export class GpsWatcher {
  private watchId: number | null = null;
  private lastFix: Fix | null = null;

  constructor(private events: GpsEvents) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.watchId !== null) {
        this.restart();
      }
    });
  }

  start(): void {
    if (!('geolocation' in navigator)) {
      this.events.onError(t('gps.none'));
      return;
    }
    if (this.watchId !== null) return;

    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.handle(pos),
      (err) => this.handleError(err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    );
  }

  stop(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  private restart(): void {
    this.stop();
    this.start();
  }

  get current(): Fix | null {
    return this.lastFix;
  }

  private handle(pos: GeolocationPosition): void {
    const c = pos.coords;
    if (c.accuracy > MAX_ACCURACY_METRES) return; // unusable, wait for lock

    let speed = c.speed;
    let heading = c.heading;

    // Derive speed/heading from movement when the platform doesn't supply them
    // (common on head units and desktop). Only trust derived heading when we
    // have actually moved - heading noise while parked makes the map spin.
    if (this.lastFix) {
      const dt = (pos.timestamp - this.lastFix.timestamp) / 1000;
      const moved = distance([this.lastFix.lng, this.lastFix.lat], [c.longitude, c.latitude]);
      if ((speed == null || Number.isNaN(speed)) && dt > 0.2) {
        speed = moved / dt;
      }
      if ((heading == null || Number.isNaN(heading)) && moved > 4) {
        heading = bearing([this.lastFix.lng, this.lastFix.lat], [c.longitude, c.latitude]);
      }
    }
    if (heading != null && Number.isNaN(heading)) heading = null;
    if (speed != null && Number.isNaN(speed)) speed = null;

    const fix: Fix = {
      lng: c.longitude,
      lat: c.latitude,
      accuracy: c.accuracy,
      speed,
      heading,
      altitude: c.altitude,
      timestamp: pos.timestamp,
    };
    this.lastFix = fix;
    this.events.onFix(fix);
  }

  private handleError(err: GeolocationPositionError): void {
    const messages: Record<number, string> = {
      [err.PERMISSION_DENIED]: t('gps.denied'),
      [err.POSITION_UNAVAILABLE]: t('gps.unavailable'),
      [err.TIMEOUT]: t('gps.waiting'),
    };
    this.events.onError(messages[err.code] ?? t('gps.error'));
  }
}
